import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI } from '../lib/api';
import { formatDate, formatDateRange, getDaysUntil, getStatusColor, getLevelColor, getOrgColor } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Calendar,
  Trophy,
  AlertTriangle,
  Clock,
  Bell,
  TrendingUp,
  Target,
  ChevronRight,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await dashboardAPI.getSummary();
      setSummary(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            Welcome back, {user?.full_name?.split(' ')[0]}
          </h1>
          <p className="text-slate-600 mt-1">{summary?.summary}</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/notifications">
            <Button variant="outline" className="relative" data-testid="notifications-btn">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
              {summary?.unread_notifications > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {summary.unread_notifications}
                </span>
              )}
            </Button>
          </Link>
          <Link to="/tournaments">
            <Button className="btn-primary" data-testid="browse-tournaments-btn">
              <Trophy className="w-4 h-4 mr-2" />
              Browse Tournaments
            </Button>
          </Link>
        </div>
      </div>

      {/* Bento Grid Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Registered Events - Large Card */}
        <Card className="card-base lg:col-span-2" data-testid="registered-events-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Target className="w-4 h-4 text-emerald-600" />
                </div>
                <CardTitle className="text-lg">Registered Events</CardTitle>
              </div>
              <Link to="/my-tournaments" className="text-sm text-primary hover:underline">
                View all <ChevronRight className="w-4 h-4 inline" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {summary?.registered_events?.length > 0 ? (
              <div className="space-y-3">
                {summary.registered_events.slice(0, 4).map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    data-testid={`registered-event-${event.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-slate-900 truncate">{event.name}</h4>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm text-slate-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDateRange(event.start_date, event.end_date)}
                        </span>
                        <span className="text-sm text-slate-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {event.location}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Badge className={getOrgColor(event.org)}>{event.org}</Badge>
                      <Badge className={getLevelColor(event.level)}>{event.level}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Target className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No registered events yet</p>
                <Link to="/tournaments" className="text-primary hover:underline text-sm">
                  Browse tournaments to get started
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Deadlines Card */}
        <Card className="card-base" data-testid="deadlines-card">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <CardTitle className="text-lg">Upcoming Deadlines</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {summary?.deadlines?.length > 0 ? (
              <div className="space-y-3">
                {summary.deadlines.map((deadline, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-amber-50 border border-amber-200 rounded-lg"
                    data-testid={`deadline-${idx}`}
                  >
                    <h4 className="font-medium text-slate-900 text-sm truncate">
                      {deadline.tournament}
                    </h4>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-slate-500">
                        {formatDate(deadline.deadline)}
                      </span>
                      <Badge
                        variant="outline"
                        className={
                          deadline.days_until <= 3
                            ? 'bg-red-100 text-red-700 border-red-200'
                            : deadline.days_until <= 7
                            ? 'bg-amber-100 text-amber-700 border-amber-200'
                            : 'bg-slate-100 text-slate-700 border-slate-200'
                        }
                      >
                        {deadline.days_until} days
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500">
                <Clock className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No upcoming deadlines</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Conflicts Card */}
        {summary?.conflicts?.length > 0 && (
          <Card className="card-base border-red-200 bg-red-50/50" data-testid="conflicts-card">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                </div>
                <CardTitle className="text-lg text-red-900">Schedule Conflicts</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {summary.conflicts.map((conflict, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-white border border-red-200 rounded-lg"
                    data-testid={`conflict-${idx}`}
                  >
                    <p className="text-sm font-medium text-slate-900">
                      {conflict.event1} <span className="text-red-600">overlaps with</span>{' '}
                      {conflict.event2}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Conflict dates: {conflict.overlap_dates}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upcoming Events */}
        <Card className="card-base lg:col-span-2" data-testid="upcoming-events-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-blue-600" />
                </div>
                <CardTitle className="text-lg">Events of Interest</CardTitle>
              </div>
              <Link to="/calendar" className="text-sm text-primary hover:underline">
                Calendar view <ChevronRight className="w-4 h-4 inline" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {summary?.upcoming_events?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {summary.upcoming_events.slice(0, 4).map((event) => (
                  <div
                    key={event.id}
                    className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    data-testid={`upcoming-event-${event.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-slate-900 text-sm">{event.name}</h4>
                      <Badge className={getStatusColor(event.tracking_status)} variant="outline">
                        {event.tracking_status}
                      </Badge>
                    </div>
                    <div className="space-y-1 text-xs text-slate-500">
                      <p className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDateRange(event.start_date, event.end_date)}
                      </p>
                      <p className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {event.location}
                      </p>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Badge className={getOrgColor(event.org)} variant="secondary">
                        {event.org}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No upcoming events tracked</p>
                <Link to="/tournaments" className="text-primary hover:underline text-sm">
                  Explore tournaments
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Results */}
        <Card className="card-base" data-testid="recent-results-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-purple-600" />
                </div>
                <CardTitle className="text-lg">Recent Results</CardTitle>
              </div>
              <Link to="/results" className="text-sm text-primary hover:underline">
                View all <ChevronRight className="w-4 h-4 inline" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {summary?.recent_results?.length > 0 ? (
              <div className="space-y-3">
                {summary.recent_results.slice(0, 3).map((result) => (
                  <div
                    key={result.id}
                    className="p-3 bg-slate-50 rounded-lg"
                    data-testid={`recent-result-${result.id}`}
                  >
                    <h4 className="font-medium text-slate-900 text-sm truncate">
                      {result.tournament_name}
                    </h4>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-slate-500">{formatDate(result.event_date)}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-primary">{result.score}</span>
                        {result.placement && (
                          <Badge variant="secondary">#{result.placement}</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500">
                <TrendingUp className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No results recorded yet</p>
                <Link to="/results" className="text-primary hover:underline text-sm">
                  Add your first result
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
